import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Users, Calendar, DollarSign, TrendingUp } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [customers, setCustomers] = useState([]);
  const [barbersList, setBarbersList] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [services, setServices] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  
  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showBarberModal, setShowBarberModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // Form states for service
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    category: 'haircut',
    price: '',
    duration: '',
    image: '',
    discountPercentage: 0,
    maxSlotsPerDay: 10,
    bufferTime: 0,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Barber image states
  const [barberImageFile, setBarberImageFile] = useState(null);
  const [barberImagePreview, setBarberImagePreview] = useState('');
  const [uploadingBarberImage, setUploadingBarberImage] = useState(false);
  
  // Form states for barber
  const [barberForm, setBarberForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    avatar: '',
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const response = await api.get('/admin/appointments');
      const appointments = response.data || [];
      
      // Group by month and calculate revenue
      const monthlyRevenue = {};
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      appointments.forEach(apt => {
        if (apt.paymentStatus === 'paid') {
          const date = new Date(apt.appointmentDate);
          const monthIndex = date.getMonth();
          const monthName = months[monthIndex];
          
          if (!monthlyRevenue[monthName]) {
            monthlyRevenue[monthName] = 0;
          }
          monthlyRevenue[monthName] += apt.finalAmount || 0;
        }
      });
      
      // Convert to chart format
      const chartDataArray = months.map(month => ({
        month,
        revenue: monthlyRevenue[month] || 0
      })).filter(item => item.revenue > 0);
      
      // If no data, show sample data
      if (chartDataArray.length === 0) {
        setChartData([{ month: 'No Data', revenue: 0 }]);
      } else {
        setChartData(chartDataArray);
      }
    } catch (err) {
      console.error('Failed to fetch chart data', err);
      setChartData([{ month: 'No Data', revenue: 0 }]);
    }
  };

  useEffect(() => {
    // Fetch data for the selected tab
    const fetchTabData = async () => {
      setTabLoading(true);
      try {
        if (activeTab === 'overview') {
          // Fetch revenue data for chart
          await fetchChartData();
        } else if (activeTab === 'customers') {
          const res = await api.get('/admin/customers');
          setCustomers(res.data || []);
        } else if (activeTab === 'barbers') {
          const res = await api.get('/admin/barbers');
          setBarbersList(res.data || []);
        } else if (activeTab === 'appointments') {
          const res = await api.get('/admin/appointments');
          setAppointments(res.data || []);
        } else if (activeTab === 'coupons') {
          const res = await api.get('/admin/coupons');
          setCoupons(res.data || []);
        } else if (activeTab === 'services') {
          const res = await api.get('/admin/services');
          setServices(res.data || []);
        }
      } catch (err) {
        console.error('Failed fetching tab data', err);
        toast.error('Failed to fetch data for this tab');
      } finally {
        setTabLoading(false);
      }
    };

    fetchTabData();
  }, [activeTab]);

  const toggleBlockCustomer = async (customerId, block = true) => {
    try {
      const url = `/admin/customers/${customerId}/${block ? 'block' : 'unblock'}`;
      await api.put(url);
      toast.success(block ? 'Customer blocked' : 'Customer unblocked');
      // Refresh list
      const res = await api.get('/admin/customers');
      setCustomers(res.data || []);
    } catch (err) {
      toast.error('Failed to update customer');
    }
  };

  const removeBarber = async (barberId) => {
    try {
      await api.delete(`/admin/barbers/${barberId}`);
      toast.success('Barber removed');
      const res = await api.get('/admin/barbers');
      setBarbersList(res.data || []);
    } catch (err) {
      toast.error('Failed to remove barber');
    }
  };

  const confirmAppointment = async (appointmentId) => {
    try {
      await api.put(`/appointments/${appointmentId}/status`, { status: 'confirmed' });
      toast.success('Appointment confirmed');
      // refresh
      const res = await api.get('/admin/appointments');
      setAppointments(res.data || []);
    } catch (err) {
      console.error('Confirm failed', err);
      toast.error('Failed to confirm appointment');
    }
  };

  const cancelAppointmentAction = async (appointmentId) => {
    try {
      const reason = window.prompt('Enter cancellation reason (optional):');
      await api.put(`/appointments/${appointmentId}/status`, { status: 'cancelled', reason });
      toast.success('Appointment cancelled');
      const res = await api.get('/admin/appointments');
      setAppointments(res.data || []);
    } catch (err) {
      console.error('Cancel failed', err);
      toast.error('Failed to cancel appointment');
    }
  };

  // Service Management Functions
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleBarberImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setBarberImageFile(file);
      setBarberImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await api.post('/admin/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      return response.data.imageUrl;
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to upload image');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const uploadBarberImage = async () => {
    if (!barberImageFile) return null;

    setUploadingBarberImage(true);
    try {
      const formData = new FormData();
      formData.append('image', barberImageFile);

      const response = await api.post('/admin/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      return response.data.imageUrl;
    } catch (error) {
      console.error('Barber image upload failed:', error);
      toast.error('Failed to upload barber image');
      return null;
    } finally {
      setUploadingBarberImage(false);
    }
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = serviceForm.image;

      // Upload image if a new file is selected
      if (imageFile) {
        const uploadedUrl = await uploadImage();
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        } else {
          toast.error('Image upload failed. Please try again.');
          return;
        }
      }

      await api.post('/admin/services', { ...serviceForm, image: imageUrl });
      toast.success('Service added successfully');
      setShowServiceModal(false);
      setServiceForm({
        name: '',
        description: '',
        category: 'haircut',
        price: '',
        duration: '',
        image: '',
        discountPercentage: 0,
        maxSlotsPerDay: 10,
        bufferTime: 0,
      });
      setImageFile(null);
      setImagePreview('');
      const res = await api.get('/admin/services');
      setServices(res.data || []);
    } catch (err) {
      toast.error('Failed to add service');
    }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await api.delete(`/admin/services/${serviceId}`);
      toast.success('Service deleted');
      const res = await api.get('/admin/services');
      setServices(res.data || []);
    } catch (err) {
      toast.error('Failed to delete service');
    }
  };

  // Barber Management Functions
  const handleBarberSubmit = async (e) => {
    e.preventDefault();
    try {
      let avatarUrl = barberForm.avatar;

      // Upload barber image if a new file is selected
      if (barberImageFile) {
        const uploadedUrl = await uploadBarberImage();
        if (uploadedUrl) {
          avatarUrl = uploadedUrl;
        } else {
          toast.error('Image upload failed. Please try again.');
          return;
        }
      }

      await api.post('/admin/barbers', { ...barberForm, avatar: avatarUrl });
      toast.success('Barber added successfully');
      setShowBarberModal(false);
      setBarberForm({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phone: '',
        avatar: '',
      });
      setBarberImageFile(null);
      setBarberImagePreview('');
      const res = await api.get('/admin/barbers');
      setBarbersList(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add barber');
    }
  };

  // Payment Management Functions
  const openPaymentModal = (appointment) => {
    setSelectedAppointment(appointment);
    setShowPaymentModal(true);
  };

  const handlePaymentUpdate = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.target);
      const paymentStatus = formData.get('paymentStatus');
      const paymentMethod = formData.get('paymentMethod');
      const transactionId = formData.get('transactionId');

      await api.put(`/admin/appointments/${selectedAppointment._id}/payment`, {
        paymentStatus,
        paymentMethod,
        transactionId,
      });
      toast.success('Payment status updated');
      setShowPaymentModal(false);
      setSelectedAppointment(null);
      // Refresh appointments and stats
      const res = await api.get('/admin/appointments');
      setAppointments(res.data || []);
      await fetchStats(); // Refresh revenue stats
    } catch (err) {
      toast.error('Failed to update payment status');
      console.error('Payment update error:', err);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-8 dark:text-white">Admin Dashboard</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Total Customers</p>
                <p className="text-3xl font-bold dark:text-white">{stats?.totalCustomers || 0}</p>
              </div>
              <Users size={32} className="text-blue-600" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Total Barbers</p>
                <p className="text-3xl font-bold dark:text-white">{stats?.totalBarbers || 0}</p>
              </div>
              <Users size={32} className="text-green-600" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Total Appointments</p>
                <p className="text-3xl font-bold dark:text-white">{stats?.totalAppointments || 0}</p>
              </div>
              <Calendar size={32} className="text-purple-600" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold text-blue-600">₹{stats?.totalRevenue || 0}</p>
              </div>
              <DollarSign size={32} className="text-orange-600" />
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-gray-300 dark:border-gray-700">
          {['overview', 'customers', 'barbers', 'services', 'appointments', 'coupons'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-semibold capitalize transition border-b-2 ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-blue-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="card">
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 dark:text-white">Monthly Revenue Overview</h2>
              {tabLoading ? (
                <p className="text-gray-600 dark:text-gray-400">Loading chart data...</p>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                      labelStyle={{ color: '#F9FAFB' }}
                      formatter={(value) => [`₹${value}`, 'Revenue']}
                    />
                    <Legend />
                    <Bar dataKey="revenue" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}

          {activeTab === 'customers' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 dark:text-white">Manage Customers</h2>
              {tabLoading ? (
                <p className="text-gray-600 dark:text-gray-400">Loading customers...</p>
              ) : customers.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400">No customers found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Name</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Email</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Status</th>
                        <th className="px-4 py-2 text-right text-sm font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {customers.map((c) => (
                        <tr key={c._id}>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{c.firstName} {c.lastName}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{c.email}</td>
                          <td className="px-4 py-3 text-sm">{c.isBlocked ? 'Blocked' : 'Active'}</td>
                          <td className="px-4 py-3 text-sm text-right">
                            {c.isBlocked ? (
                              <button onClick={() => toggleBlockCustomer(c._id, false)} className="px-3 py-1 bg-green-600 text-white rounded">Unblock</button>
                            ) : (
                              <button onClick={() => toggleBlockCustomer(c._id, true)} className="px-3 py-1 bg-red-600 text-white rounded">Block</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'barbers' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold dark:text-white">Manage Barbers</h2>
                <button
                  onClick={() => setShowBarberModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  + Add Barber
                </button>
              </div>
              {tabLoading ? (
                <p className="text-gray-600 dark:text-gray-400">Loading barbers...</p>
              ) : barbersList.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400">No barbers found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Avatar</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Name</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Email</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Joined</th>
                        <th className="px-4 py-2 text-right text-sm font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {barbersList.map((b) => (
                        <tr key={b._id}>
                          <td className="px-4 py-3">
                            {b.avatar ? (
                              <img 
                                src={b.avatar} 
                                alt={b.firstName}
                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 font-semibold">
                                {b.firstName[0]}{b.lastName[0]}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{b.firstName} {b.lastName}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{b.email}</td>
                          <td className="px-4 py-3 text-sm">{new Date(b.createdAt).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-sm text-right">
                            <button onClick={() => removeBarber(b._id)} className="px-3 py-1 bg-red-600 text-white rounded">Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold dark:text-white">Manage Services</h2>
                <button
                  onClick={() => setShowServiceModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  + Add Service
                </button>
              </div>
              {tabLoading ? (
                <p className="text-gray-600 dark:text-gray-400">Loading services...</p>
              ) : services.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400">No services found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Name</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Category</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Price</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Duration</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Status</th>
                        <th className="px-4 py-2 text-right text-sm font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {services.map((s) => (
                        <tr key={s._id}>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{s.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 capitalize">{s.category}</td>
                          <td className="px-4 py-3 text-sm">₹{s.price}</td>
                          <td className="px-4 py-3 text-sm">{s.duration} min</td>
                          <td className="px-4 py-3 text-sm">{s.isActive ? 'Active' : 'Inactive'}</td>
                          <td className="px-4 py-3 text-sm text-right">
                            <button onClick={() => deleteService(s._id)} className="px-3 py-1 bg-red-600 text-white rounded">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'appointments' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 dark:text-white">View Appointments</h2>
              {tabLoading ? (
                <p className="text-gray-600 dark:text-gray-400">Loading appointments...</p>
              ) : appointments.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400">No appointments found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Date</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Customer</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Barber</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Service</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Amount</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Payment</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Status</th>
                        <th className="px-4 py-2 text-right text-sm font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {appointments.map((a) => (
                        <tr key={a._id}>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{new Date(a.appointmentDate).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{a.customer?.firstName} {a.customer?.lastName}</td>
                          <td className="px-4 py-3 text-sm">{a.barber?.firstName} {a.barber?.lastName}</td>
                          <td className="px-4 py-3 text-sm">{a.service?.name}</td>
                          <td className="px-4 py-3 text-sm">₹{a.finalAmount}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded text-xs ${a.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {a.paymentStatus}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">{a.status}</td>
                          <td className="px-4 py-3 text-sm text-right">
                            {a.status !== 'cancelled' && (
                              <button onClick={() => openPaymentModal(a)} className="px-3 py-1 bg-purple-600 text-white rounded mr-2">Payment</button>
                            )}
                            {a.status !== 'confirmed' && a.status !== 'completed' && a.status !== 'cancelled' && (
                              <button onClick={() => confirmAppointment(a._id)} className="px-3 py-1 bg-green-600 text-white rounded mr-2">Confirm</button>
                            )}
                            {a.status !== 'cancelled' && a.paymentStatus !== 'paid' && (
                              <button onClick={() => cancelAppointmentAction(a._id)} className="px-3 py-1 bg-red-600 text-white rounded">Cancel</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'coupons' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 dark:text-white">Manage Coupons</h2>
              {tabLoading ? (
                <p className="text-gray-600 dark:text-gray-400">Loading coupons...</p>
              ) : coupons.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400">No coupons found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Code</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Discount</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Valid From</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Valid Upto</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {coupons.map((c) => (
                        <tr key={c._id}>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{c.code}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{c.discountType === 'percent' ? `${c.discountValue}%` : `₹${c.discountValue}`}</td>
                          <td className="px-4 py-3 text-sm">{c.validFrom ? new Date(c.validFrom).toLocaleDateString() : '-'}</td>
                          <td className="px-4 py-3 text-sm">{c.validUpto ? new Date(c.validUpto).toLocaleDateString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Service Modal */}
        {showServiceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4 dark:text-white">Add New Service</h3>
              <form onSubmit={handleServiceSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Name</label>
                  <input
                    type="text"
                    required
                    value={serviceForm.name}
                    onChange={(e) => setServiceForm({...serviceForm, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Description</label>
                  <textarea
                    required
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    rows="3"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Category</label>
                  <select
                    value={serviceForm.category}
                    onChange={(e) => setServiceForm({...serviceForm, category: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="haircut">Haircut</option>
                    <option value="beard-trim">Beard Trim</option>
                    <option value="facial">Facial</option>
                    <option value="massage">Massage</option>
                    <option value="hair-color">Hair Color</option>
                    <option value="groom-package">Groom Package</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Price (₹)</label>
                  <input
                    type="number"
                    required
                    value={serviceForm.price}
                    onChange={(e) => setServiceForm({...serviceForm, price: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Duration (minutes)</label>
                  <input
                    type="number"
                    required
                    value={serviceForm.duration}
                    onChange={(e) => setServiceForm({...serviceForm, duration: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Service Image</label>
                  
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="mb-3 relative">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded-lg border-2 border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview('');
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                        title="Remove image"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  {/* File Upload */}
                  <div className="flex items-center space-x-3">
                    <label className="flex-1 cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          {imageFile ? imageFile.name : 'Click to upload or drag and drop'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">PNG, JPG, GIF up to 5MB</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {/* OR Divider */}
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">OR</span>
                    </div>
                  </div>

                  {/* URL Input */}
                  <input
                    type="url"
                    value={serviceForm.image}
                    onChange={(e) => setServiceForm({...serviceForm, image: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="Enter image URL"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={uploadingImage}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadingImage ? 'Uploading...' : 'Add Service'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowServiceModal(false);
                      setImageFile(null);
                      setImagePreview('');
                    }}
                    className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Barber Modal */}
        {showBarberModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
              <h3 className="text-xl font-bold mb-4 dark:text-white">Add New Barber</h3>
              <form onSubmit={handleBarberSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">First Name</label>
                  <input
                    type="text"
                    required
                    value={barberForm.firstName}
                    onChange={(e) => setBarberForm({...barberForm, firstName: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Last Name</label>
                  <input
                    type="text"
                    required
                    value={barberForm.lastName}
                    onChange={(e) => setBarberForm({...barberForm, lastName: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Email</label>
                  <input
                    type="email"
                    required
                    value={barberForm.email}
                    onChange={(e) => setBarberForm({...barberForm, email: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Phone</label>
                  <input
                    type="tel"
                    required
                    value={barberForm.phone}
                    onChange={(e) => setBarberForm({...barberForm, phone: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Password</label>
                  <input
                    type="password"
                    required
                    value={barberForm.password}
                    onChange={(e) => setBarberForm({...barberForm, password: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Barber Photo</label>
                  
                  {/* Image Preview */}
                  {barberImagePreview && (
                    <div className="mb-3 relative">
                      <img 
                        src={barberImagePreview} 
                        alt="Preview" 
                        className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setBarberImageFile(null);
                          setBarberImagePreview('');
                        }}
                        className="absolute top-0 right-1/2 translate-x-16 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        title="Remove image"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  {/* File Upload */}
                  <div className="flex items-center space-x-3">
                    <label className="flex-1 cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          {barberImageFile ? barberImageFile.name : 'Click to upload barber photo'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">PNG, JPG, GIF up to 5MB</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBarberImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {/* OR Divider */}
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">OR</span>
                    </div>
                  </div>

                  {/* URL Input */}
                  <input
                    type="url"
                    value={barberForm.avatar}
                    onChange={(e) => setBarberForm({...barberForm, avatar: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="Enter image URL"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={uploadingBarberImage}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadingBarberImage ? 'Uploading...' : 'Add Barber'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowBarberModal(false);
                      setBarberImageFile(null);
                      setBarberImagePreview('');
                    }}
                    className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Payment Update Modal */}
        {showPaymentModal && selectedAppointment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
              <h3 className="text-xl font-bold mb-4 dark:text-white">Update Payment Status</h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">Customer: {selectedAppointment.customer?.firstName} {selectedAppointment.customer?.lastName}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Amount: ₹{selectedAppointment.finalAmount}</p>
              </div>
              <form onSubmit={handlePaymentUpdate}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Payment Status</label>
                  <select
                    name="paymentStatus"
                    required
                    defaultValue={selectedAppointment.paymentStatus}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Payment Method</label>
                  <select
                    name="paymentMethod"
                    required
                    defaultValue={selectedAppointment.paymentMethod}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="online">Online</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 dark:text-gray-300">Transaction ID (optional)</label>
                  <input
                    type="text"
                    name="transactionId"
                    defaultValue={selectedAppointment.transactionId || ''}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="TXN123456"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                  >
                    Update Payment
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
